import React, { useState, useEffect, useRef } from 'react';
import { saveReview, supabase, reportQuestion } from './supabaseClient';
import { QadderLogo } from './Logo';
import { useLanguage } from './LanguageContext';

// ══════════════════════════════════════════════════════════
//  مكوّن الإبلاغ عن مشكلة — مشترك بين TrialQuiz وStudyScreen
// ══════════════════════════════════════════════════════════
const REPORT_TYPES_TQ = [
  { id: 'other', icon: '💬', label: 'ملاحظاتك على السؤال', color: 'var(--text-secondary)' },
];

function TrialReportButton({ questionId }) {
  const { t } = useLanguage();
  const [open,      setOpen]      = useState(false);
  const [selected,  setSelected]  = useState(null);
  const [note,      setNote]      = useState('');
  const [status,    setStatus]    = useState('idle'); // idle | loading | error
  const [showPopup, setShowPopup] = useState(false);

  const handleSubmit = async () => {
    if (!selected || status !== 'idle') return;
    setStatus('loading');
    const { error } = await reportQuestion(questionId, null, selected, note);
    if (!error) {
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setOpen(false);
        setSelected(null);
        setNote('');
        setStatus('idle');
      }, 1800);
    } else {
      console.error('[report]', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <>
      {/* بوب-أب تأكيد الإرسال */}
      {showPopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--bg-page)',
            border: '1px solid rgba(34,197,94,0.35)',
            borderRadius: 22, padding: '36px 48px',
            textAlign: 'center', fontFamily: 'Tajawal,sans-serif',
            boxShadow: '0 8px 48px rgba(0,0,0,0.7)',
          }}>
            <div style={{ fontSize: 56, marginBottom: 14 }}>✅</div>
            <div style={{ color: '#86efac', fontSize: 22, fontWeight: 900 }}>{t('trial_sent')}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>
              {t('trial_thanks')}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <button onClick={() => setOpen(p => !p)} style={{
          width: '100%', background: 'transparent',
          border: '1px solid #1e2a42', borderRadius: open ? '10px 10px 0 0' : 10,
          padding: '8px 14px', cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{t('trial_reportProblem')}</span>
          <span style={{ color: 'var(--border)', fontSize: 11 }}>{open ? '▲' : '▼'}</span>
        </button>

        {open && (
          <div style={{
            background: '#0a0f1a', border: '1px solid #1e2a42',
            borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '12px 14px',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 10 }}>
              {REPORT_TYPES_TQ.map(type => {
                const active = selected === type.id;
                return (
                  <button key={type.id} onClick={() => setSelected(type.id)} style={{
                    background: active ? type.color + '15' : 'transparent',
                    border: `1px solid ${active ? type.color + '55' : 'var(--border)'}`,
                    borderRadius: 8, padding: '8px 12px', cursor: 'pointer',
                    fontFamily: 'Tajawal,sans-serif',
                    display: 'flex', alignItems: 'center', gap: 8, textAlign: 'right',
                  }}>
                    <span style={{ fontSize: 15 }}>{type.icon}</span>
                    <span style={{
                      color: active ? type.color : 'var(--text-secondary)',
                      fontSize: 13, fontWeight: active ? 700 : 400, flex: 1,
                    }}>{type.label}</span>
                    {active && <span style={{ color: type.color }}>✓</span>}
                  </button>
                );
              })}
            </div>
            {selected && (
              <>
                <textarea value={note} onChange={e => setNote(e.target.value)}
                  placeholder={t('trial_additionalDetails')} maxLength={200} rows={2}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: 'var(--bg-card)', border: '1px solid #1e2a42',
                    borderRadius: 8, padding: '8px 10px', color: 'var(--text)',
                    fontSize: 12, fontFamily: 'Tajawal,sans-serif',
                    resize: 'none', outline: 'none', marginBottom: 8, direction: 'rtl',
                  }}
                />
                <button onClick={handleSubmit} disabled={status === 'loading'} style={{
                  width: '100%', padding: '10px',
                  background: status === 'loading' ? 'var(--border)' : 'linear-gradient(135deg,#7c6df8,#a78bfa)',
                  border: 'none', borderRadius: 8, color: '#fff',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                }}>
                  {status === 'loading' ? t('trial_reportSending') : status === 'error' ? t('trial_reportFailed') : t('trial_sendReport')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}


const SECTION_IDS = {
  كمي:      '3b546880-a75d-4f68-af2c-78ecfa558539',
  لفظي:     '1461664b-a8ee-4069-825f-22147b5dc403',
  رياضيات:  '18ef5d52-dade-449f-89bb-6fc94fab3520',
  علوم:     'b25e9099-d77b-4e98-b504-db13fd519733', // فيزياء + كيمياء + أحياء
};

const TAHSILI_SUBJECTS = [
  { id: 'رياضيات', icon: '📐', color: '#3b82f6' },
  { id: 'فيزياء',  icon: '⚡', color: '#f59e0b' },
  { id: 'كيمياء',  icon: '⚗️', color: '#10b981' },
  { id: 'أحياء',   icon: '🧬', color: '#ec4899' },
];

// بنك التجريبي — يُحمَّل مرة واحدة عند فتح الصفحة
// 50 سؤال لكل قسم → كل جلسة تأخذ 20 عشوائي منها
const trialPool = { كمي: [], لفظي: [] };
let poolLoaded = false;

function mapRow(row, type) {
  return {
    id:        row.id,
    text:      row.question_ar,
    options:   Array.isArray(row.options) ? row.options : JSON.parse(row.options || '[]'),
    answer:    parseInt(row.correct_key ?? 0, 10),
    explain:   row.explanation || '',
    image_url: row.image_url || null,
    subject:   row.subject || null,
    type,
  };
}

async function loadTrialPool() {
  if (poolLoaded) return true;
  try {
    const [r1, r2] = await Promise.all([
      supabase
        .from('questions')
        .select('id, question_ar, options, correct_key, explanation, image_url')
        .eq('section_id', SECTION_IDS.كمي)
        .limit(100),
      supabase
        .from('questions')
        .select('id, question_ar, options, correct_key, explanation, image_url')
        .eq('section_id', SECTION_IDS.لفظي)
        .limit(100),
    ]);

    // أسئلة تشير لصورة/شكل لكن ما عندها image_url → نحذفها
    const needsImage = (text) =>
      /الشكل|الرسم البياني|المجاور|الصورة التالية|الشكل التالي/.test(text || '');

    const isValidOpt = (o) => {
      const s = String(o || '').trim();
      const low = s.toLowerCase();
      return (
        s.length > 2 &&
        !/^[.\s،,…]+$/.test(s) &&
        low !== 'blank' &&
        low !== 'null' &&
        low !== 'undefined' &&
        low !== 'n/a' &&
        s !== '-'
      );
    };
    const needsPassage = (t) => /النص|الفقرة|المقطع|القطعة/.test(t || '');
    const clean = (q) =>
      q.text?.trim() &&
      q.options.length === 4 &&
      q.options.every(o => typeof o === 'string' && isValidOpt(o)) &&
      !needsPassage(q.text) &&
      (!needsImage(q.text) || !!q.image_url);  // لو يحتاج صورة لازم تكون موجودة

    if (r1.data?.length) {
      trialPool.كمي = r1.data.map(r => mapRow(r, 'كمي')).filter(clean);
    }
    if (r2.data?.length) {
      trialPool.لفظي = r2.data.map(r => mapRow(r, 'لفظي')).filter(clean);
    }

    poolLoaded = trialPool.كمي.length > 0 || trialPool.لفظي.length > 0;
    console.log(`[trialPool] كمي=${trialPool.كمي.length} | لفظي=${trialPool.لفظي.length}`);
    return poolLoaded;
  } catch (e) {
    console.error('[loadTrialPool]', e);
    return false;
  }
}

function pickFromPool(type, count = 20) {
  const pool = trialPool[type] || [];
  if (!pool.length) return [];
  return [...pool].sort(() => Math.random() - 0.5).slice(0, Math.min(count, pool.length));
}

const bank = {
  كمي: [
    { text: "إذا كان 3س + 7 = 22، فما قيمة س؟", options: ["3","4","5","6"], answer: 2, explain: "3س = 15 ← س = 5" },
    { text: "متجر خفّض سعر منتج من 80 إلى 60 ريال، ما نسبة الخصم؟", options: ["20%","25%","30%","15%"], answer: 1, explain: "(80-60)÷80 × 100 = 25%" },
    { text: "ما الناتج: (√144) + (√25)؟", options: ["17","19","13","15"], answer: 0, explain: "√144=12 و √25=5 ← 17" },
    { text: "نسبة طلاب إلى طالبات 3:2 والمجموع 60، كم عدد الطلاب؟", options: ["24","30","36","40"], answer: 2, explain: "(3÷5)×60 = 36" },
    { text: "مستطيل طوله 12 سم وعرضه 8 سم، ما مساحته؟", options: ["40","80","96","48"], answer: 2, explain: "12×8 = 96 سم²" },
    { text: "ما قيمة: 2⁵ ÷ 2³؟", options: ["2","4","8","16"], answer: 1, explain: "2⁵÷2³ = 2² = 4" },
    { text: "ما الرقم التالي: 2، 6، 18، 54، ___؟", options: ["108","162","216","144"], answer: 1, explain: "كل رقم × 3 ← 54×3=162" },
    { text: "متوسط: 4، 8، 12، 16، 20 يساوي؟", options: ["10","11","12","13"], answer: 2, explain: "المجموع 60 ÷ 5 = 12" },
  ],
  لفظي: [
    { text: "ما مرادف كلمة 'عَسير'؟", options: ["سهل","صعب","بعيد","قريب"], answer: 1, explain: "عسير = صعب وشاق" },
    { text: "ما ضد كلمة 'مُقتَصِد'؟", options: ["كريم","بخيل","مُسرِف","غني"], answer: 2, explain: "ضد المقتصد هو المسرف" },
    { text: "أكمل التناظر: طبيب : مستشفى ← معلم : ___", options: ["طالب","كتاب","مدرسة","علم"], answer: 2, explain: "الطبيب في مستشفى كالمعلم في مدرسة" },
    { text: "الكلمة الشاذة: (أسد - نمر - ذئب - نسر - فهد)", options: ["أسد","نمر","نسر","فهد"], answer: 2, explain: "النسر طائر والباقي حيوانات أرضية" },
    { text: "حدّد الخطأ: 'ذهبتُ إلى السوقَ واشتريتُ كتاباً'", options: ["ذهبتُ","السوقَ","اشتريتُ","كتاباً"], answer: 1, explain: "السوق مجرورة بـ'إلى': السوقِ" },
    { text: "أكمل التناظر: ماء : عطش ← طعام : ___", options: ["أكل","جوع","شبع","مطبخ"], answer: 1, explain: "الماء يزيل العطش كالطعام يزيل الجوع" },
    { text: "الكلمة الشاذة: (رمضان - ذو الحجة - شعبان - مارس - صفر)", options: ["رمضان","ذو الحجة","مارس","صفر"], answer: 2, explain: "مارس شهر ميلادي والباقي هجرية" },
    { text: "ما مرادف 'نَهِم'؟", options: ["شبعان","شَرِه","نائم","متأخر"], answer: 1, explain: "النَّهِم: الشديد الشهوة للأكل" },
  ],
  تحصيلي: [
    { text: "ما ناتج: log₂(32)؟", options: ["4","5","6","3"], answer: 1, explain: "2⁵=32 ← log₂(32)=5", subject:"رياضيات" },
    { text: "أيّ المعادلات يمثّل قانون أوم؟", options: ["P=IV","V=IR","F=ma","E=mc²"], answer: 1, explain: "V=IR: الجهد = الشدة × المقاومة", subject:"فيزياء" },
    { text: "عدد الكروموسومات في الخلية الجسمية البشرية؟", options: ["23","44","46","48"], answer: 2, explain: "23 زوج = 46 كروموسوم", subject:"أحياء" },
    { text: "HCl + NaOH ينتج؟", options: ["NaCl + H₂O","NaH + ClO","Na + HCl","NaO + H₂"], answer: 0, explain: "تفاعل تعادل: ملح + ماء", subject:"كيمياء" },
    { text: "متى فُتحت مكة المكرمة؟", options: ["6 هـ","7 هـ","8 هـ","9 هـ"], answer: 2, explain: "فتح مكة رمضان عام 8 هـ", subject:"تاريخ" },
    { text: "مشتقة f(x) = 3x² + 2x؟", options: ["6x","6x+2","3x+2","6x²"], answer: 1, explain: "f'(x) = 6x + 2", subject:"رياضيات" },
  ],
};

const TYPES = [
  { id:"الكل",       icon:"📚", color:"#7c6df8", label:"قدرات" },
  { id:"تحصيلي",     icon:"🎓", color:"#10b981", label:"تحصيلي" },
  { id:"tajamuat",   icon:"🗂️", color:"#e879f9", label:"تجمعات محدث" },
  { id:"step",       icon:"🌍", color:"#f59e0b", label:"STEP",         comingSoon: true },
];

const COLORS = { كمي:"#3b82f6", لفظي:"#7c6df8", تحصيلي:"#10b981" };
const ICONS  = { كمي:"🔢", لفظي:"📖", تحصيلي:"🎓" };
const TIME_BY_TYPE = { كمي:70, لفظي:60, تحصيلي:70 };

function shuffle(a){ return [...a].sort(()=>Math.random()-.5); }

function buildQuiz(type){
  const pool = type==="الكل"
    ? [
        ...bank.كمي.map(q=>({...q,type:"كمي"})),
        ...bank.لفظي.map(q=>({...q,type:"لفظي"})),
        ...bank.تحصيلي.map(q=>({...q,type:"تحصيلي"}))
      ]
    : bank[type].map(q=>({...q,type}));
  return shuffle(pool).slice(0, Math.min(20, pool.length));
}

const TRIAL_KEY = 'qadder_trial_progress';

function QuestionGrid({ quiz, answers, cur, onNavigate, onClose }) {
  const answeredCount = Object.keys(answers).length;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-page)',
          border: '1px solid #334155', borderRadius: 22,
          padding: '22px 20px', width: '100%', maxWidth: 400,
          direction: 'rtl', fontFamily: 'Tajawal,sans-serif',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: 'var(--text)', margin: 0, fontSize: 16, fontWeight: 900 }}>
            الأسئلة ({answeredCount}/{quiz.length})
          </h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-secondary)',
            cursor: 'pointer', fontSize: 20, padding: 0, lineHeight: 1,
          }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {[['#3b82f6','الحالي'],['#22c55e','صحيح'],['#ef4444','خطأ'],['var(--text-muted)','لم يُجب']].map(([c,l]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 12, height: 12, borderRadius: 4, background: c }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{l}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {quiz.map((_, i) => {
            const ans = answers[i];
            const isCur = i === cur;
            let bg = 'var(--text-muted)';
            if (isCur) bg = '#3b82f6';
            else if (ans?.isCorrect === true) bg = '#22c55e';
            else if (ans?.isCorrect === false) bg = '#ef4444';
            return (
              <button key={i} onClick={() => onNavigate(i)} style={{
                width: 42, height: 42, borderRadius: 10, background: bg,
                border: isCur ? '2px solid #fff' : '2px solid transparent',
                color: '#fff', fontSize: 14, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                transition: 'all .15s',
              }}>{i + 1}</button>
            );
          })}
        </div>

        <div style={{
          marginTop: 16, padding: '10px 14px',
          background: 'var(--bg-card-alt)', borderRadius: 12,
          display: 'flex', justifyContent: 'space-around',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#22c55e', fontWeight: 900, fontSize: 18 }}>
              {Object.values(answers).filter(a => a.isCorrect).length}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>صحيح</div>
          </div>
          <div style={{ width: 1, background: 'var(--border)' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#ef4444', fontWeight: 900, fontSize: 18 }}>
              {Object.values(answers).filter(a => !a.isCorrect).length}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>خطأ</div>
          </div>
          <div style={{ width: 1, background: 'var(--border)' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--text-secondary)', fontWeight: 900, fontSize: 18 }}>
              {quiz.length - answeredCount}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>متبقي</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuestionRatingTrial({ qKey, rated, onRate }) {
  const { t } = useLanguage();
  if (!qKey) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: 10 }}>
      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{t('trial_questionCorrect')}</span>
      <button
        onClick={() => !rated && onRate(qKey, 'up')}
        style={{
          background: rated === 'up' ? 'rgba(34,197,94,0.15)' : 'var(--bg-card)',
          border: `1px solid ${rated === 'up' ? '#22c55e' : '#334155'}`,
          borderRadius: 8, padding: '5px 14px',
          cursor: rated ? 'default' : 'pointer',
          color: rated === 'up' ? '#22c55e' : 'var(--text-secondary)',
          fontSize: 16, fontFamily: 'Tajawal,sans-serif', transition: 'all .2s',
        }}
      >👍</button>
      <button
        onClick={() => !rated && onRate(qKey, 'down')}
        style={{
          background: rated === 'down' ? 'rgba(239,68,68,0.15)' : 'var(--bg-card)',
          border: `1px solid ${rated === 'down' ? '#ef4444' : '#334155'}`,
          borderRadius: 8, padding: '5px 14px',
          cursor: rated ? 'default' : 'pointer',
          color: rated === 'down' ? '#ef4444' : 'var(--text-secondary)',
          fontSize: 16, fontFamily: 'Tajawal,sans-serif', transition: 'all .2s',
        }}
      >👎</button>
      {rated && (
        <span style={{ color: rated === 'up' ? '#22c55e' : '#f87171', fontSize: 12 }}>
          {rated === 'up' ? 'شكراً! ✓' : 'تم الإبلاغ ✓'}
        </span>
      )}
    </div>
  );
}

export default function TrialQuiz({ onDone, onSkip, onLogin, theme, setTheme }) {
  const { t, isRTL } = useLanguage();
  const [screen, setScreen] = useState("home");
  const [mode, setMode] = useState("الكل");
  const [aiAnalysis,  setAiAnalysis]  = useState('');
  const [aiLoading,   setAiLoading]   = useState(false);
  const [quiz, setQuiz] = useState([]);
  const [cur, setCur] = useState(0);
  const [sel, setSel] = useState(null);
  const [showAns, setShowAns] = useState(false);
  const [answers, setAnswers] = useState({});
  const answersRef = useRef({});
  const [time, setTime] = useState(60);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [tahsiliSubject, setTahsiliSubject] = useState(null); // null = الكل
  const [qudratSub, setQudratSub] = useState(null); // null = الكل، 'كمي'، 'لفظي'
  const [stars, setStars]         = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [comment, setComment]     = useState('');
  const [reviewName, setReviewName] = useState('');
  const [reviewStage, setReviewStage] = useState('');
  const [reviewSpec,  setReviewSpec]  = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const SPECS_TQ = {
    'ثانوي علمي': ['علوم طبيعية', 'رياضيات وحاسب'],
    'ثانوي أدبي': ['آداب وإنسانيات', 'إدارة الأعمال'],
  };
  const [hint, setHint]               = useState('');
  const [hintLoading, setHintLoading] = useState(false);
  const [ratings, setRatings]         = useState({});

  const handleRate = async (questionId, rating) => {
    const key = questionId || `idx_${cur}`;
    if (ratings[key]) return;
    setRatings(prev => ({ ...prev, [key]: rating }));
    try {
      if (questionId) {
        await supabase.from('question_reports').insert({
          question_id: questionId,
          report_type: `rating_${rating}`,
        });
        if (rating === 'down') {
          const { count } = await supabase
            .from('question_reports')
            .select('id', { count: 'exact', head: true })
            .eq('question_id', questionId)
            .eq('report_type', 'rating_down');
          if (count >= 3) {
            await supabase.from('questions').update({
              is_active: false,
              flag_reason: 'auto_hidden_low_rating',
            }).eq('id', questionId);
          }
        }
      }
    } catch (e) { /* silent */ }
  };

  // تحميل بنك التجريبي عند فتح الصفحة
  useEffect(() => {
    loadTrialPool();
    // تنظيف cache hint_undefined الخاطئ
    localStorage.removeItem('hint_undefined');
  }, []);

  const [savedTrial] = useState(() => {
    try {
      const raw = localStorage.getItem(TRIAL_KEY);
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (Array.isArray(p.quiz) && p.quiz.length && Date.now() - p.savedAt < 86400000)
        return p;
      localStorage.removeItem(TRIAL_KEY);
    } catch {}
    return null;
  });

  const resumeTrial = () => {
    const p = savedTrial;
    // backward compat: old format had results array
    const savedAnswers = p.answers ?? (
      Array.isArray(p.results)
        ? Object.fromEntries(p.results.map((r, i) => [i, { selected: -1, isCorrect: r.correct }]))
        : {}
    );
    let nextCur = 0;
    for (let i = 0; i < p.quiz.length; i++) {
      if (savedAnswers[i] === undefined) { nextCur = i; break; }
    }
    setQuiz(p.quiz);
    setCur(nextCur);
    answersRef.current = savedAnswers;
    setAnswers(savedAnswers);
    setSel(null); setShowAns(false);
    setTime(TIME_BY_TYPE[p.quiz[nextCur]?.type] || 60);
    setScreen("quiz");
  };

  useEffect(() => {
    if (screen !== "quiz" || showAns) return;
    if (time === 0) { submit(null); return; }
    const t = setTimeout(() => setTime(x => x - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time, screen, showAns]);

  const startQuiz = async () => {
    if (mode === "tajamuat") { onSkip(); return; }
    localStorage.removeItem(TRIAL_KEY);
    setScreen("loading");

    try {
      let questions = [];

      if (mode === "تحصيلي") {
        // جلب من Supabase حسب المادة المختارة
        let query = supabase
          .from('questions')
          .select('id, question_ar, options, correct_key, explanation, subject, image_url')
          .eq('is_active', true)
          .limit(150);

        if (!tahsiliSubject) {
          // الكل — جميع مواد التحصيلي
          query = query.in('subject', ['رياضيات', 'فيزياء', 'كيمياء', 'أحياء', 'علوم']);
        } else {
          query = query.eq('subject', tahsiliSubject);
        }

        const { data } = await query;
        if (data?.length) {
          questions = data
            .map(r => mapRow(r, 'تحصيلي'))
            .filter(q => q.text?.trim() && q.options.length === 4 && !(/النص|الفقرة|المقطع|القطعة|الجدول/.test(q.text)) && q.options.every(o => {
              const s = String(o || '').trim();
              return s.length >= 1 && !/^[.\s،,…]+$/.test(s);
            }))
            .sort(() => Math.random() - 0.5)
            .slice(0, 20);
        }
        if (!questions.length) {
          // fallback: من البنك المدمج مع فلترة المادة المختارة
          const fallback = buildQuiz('تحصيلي');
          questions = tahsiliSubject
            ? fallback.filter(q => q.subject === tahsiliSubject)
            : fallback;
          if (!questions.length) questions = fallback; // إذا ما في fallback بالمادة خذ الكل
        }

      } else {
        // كمي / لفظي / الكل
        if (!poolLoaded) await loadTrialPool();

        if (mode === "الكل") {
          if (qudratSub) {
            // قسم محدد (كمي أو لفظي)
            questions = pickFromPool(qudratSub, 20);
            if (!questions.length) questions = buildQuiz(qudratSub);
          } else {
            // الكل — 10 كمي + 10 لفظي
            const kammi = pickFromPool('كمي', 10);
            const lafzi = pickFromPool('لفظي', 10);
            questions = [...kammi, ...lafzi].sort(() => Math.random() - 0.5);
          }
        } else if (trialPool[mode]) {
          questions = pickFromPool(mode, 20);
        }

        if (!questions.length) questions = buildQuiz(qudratSub || mode);
      }

      setQuiz(questions.slice(0, 20));
    } catch(e) {
      console.error('startQuiz error:', e);
      setQuiz(buildQuiz(mode));
    }

    setCur(0); setSel(null); setShowAns(false);
    setAnswers({}); answersRef.current = {};
    setScreen("quiz");
  };

  const generateHint = async (q) => {
    if (!q) return;
    // إذا عندنا شرح مباشرة من قاعدة البيانات نعرضه فوراً
    if (q.explain && !q.explain.includes('المصدر') && !q.explain.includes('تسريبات')) {
      setHint(q.explain); setHintLoading(false); return;
    }
    try {
      const cacheKey = q.id ? `hint_${q.id}` : `hint_txt_${(q.text||'').slice(0,40)}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) { setHint(cached); setHintLoading(false); return; }
      setHint(''); setHintLoading(true);
      const res = await fetch('/api/hint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: q.text,
          options: Array.isArray(q.options) ? q.options : [],
          correctIndex: q.answer,
          subject: q.type || '',
        }),
      });
      const data = await res.json();
      const h = data?.hint || '';
      setHint(h);
      if (h) localStorage.setItem(cacheKey, h);
    } catch {
      setHint('تعذّر توليد الشرح');
    } finally {
      setHintLoading(false);
    }
  };

  const navigateTo = (idx) => {
    if (idx < 0 || idx >= quiz.length) return;
    setShowGrid(false);
    setHint('');
    const existing = answersRef.current[idx];
    setCur(idx);
    if (existing !== undefined) {
      setSel(existing.selected);
      setShowAns(true);
      generateHint(quiz[idx]);
    } else {
      setSel(null);
      setShowAns(false);
      setTime(TIME_BY_TYPE[quiz[idx]?.type] || 60);
    }
  };

  const submit = (i) => {
    if (answersRef.current[cur] !== undefined) return;
    const chosen = i ?? -1;
    const isCorrect = quiz[cur].answer === chosen;
    setSel(chosen); setShowAns(true);
    generateHint(quiz[cur]);
    const newAnswers = { ...answersRef.current, [cur]: { selected: chosen, isCorrect } };
    answersRef.current = newAnswers;
    setAnswers(newAnswers);
    localStorage.setItem(TRIAL_KEY, JSON.stringify({
      mode, quiz, answers: newAnswers, savedAt: Date.now(),
    }));
  };

  const next = () => {
    if (Object.keys(answersRef.current).length >= quiz.length) {
      localStorage.removeItem(TRIAL_KEY);
      setScreen("result");
      return;
    }
    for (let i = cur + 1; i < quiz.length; i++) {
      if (answersRef.current[i] === undefined) { navigateTo(i); return; }
    }
    for (let i = 0; i < cur; i++) {
      if (answersRef.current[i] === undefined) { navigateTo(i); return; }
    }
    localStorage.removeItem(TRIAL_KEY);
    setScreen("result");
  };

  const answeredCount = Object.keys(answers).length;
  const score = Object.values(answers).filter(a => a.isCorrect).length;
  const pct = quiz.length ? Math.round((score / quiz.length) * 100) : 0;
  const timerC = time > 20 ? "#22c55e" : time > 10 ? "#f59e0b" : "#ef4444";

  const ThemeBtn = () => null;

  const wrap = (children, maxW = 480) => (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg-page)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      fontFamily: "Tajawal,sans-serif", direction: isRTL ? "rtl" : "ltr", padding: 24,
      color: 'var(--text)',
    }}>
      <ThemeBtn />
      <div style={{ width: "100%", maxWidth: maxW }}>{children}</div>
    </div>
  );

  // ── Home ──
  if (screen === "home") return (
    <div style={{
      minHeight: "100vh", background: "var(--bg-page)",
      display: "flex", flexDirection: "column",
      fontFamily: "Tajawal,sans-serif", direction: isRTL ? "rtl" : "ltr", color: 'var(--text)',
    }}>
      {/* شريط علوي */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #1e2a42', background: 'rgba(13,17,23,0.95)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <QadderLogo onHome={onSkip} size={30} />
        {onSkip && (
          <button onClick={onSkip} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '7px 14px', color: 'var(--text-secondary)',
            fontSize: 13, fontFamily: 'Tajawal,sans-serif', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>{t('trial_home')}</button>
        )}
      </div>
      {/* محتوى متوسط */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 480 }}>
      <div>

      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <h2 style={{
          color: "var(--text)", fontSize: 26, fontWeight: 900, margin: "0 0 6px",
          background: "linear-gradient(90deg,#7c6df8,#a78bfa)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>{t('trial_title')}</h2>
        <p style={{ color: "var(--text-secondary)", fontSize: 14, margin: 0 }}>{t('trial_subtitle')}</p>
      </div>

      {savedTrial && (
        <div style={{
          background: 'rgba(129,140,248,0.08)', border: '1px solid rgba(129,140,248,0.25)',
          borderRadius: 14, padding: '14px 16px', marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14 }}>{t('trial_savedSession')}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>
              {t('trial_answered')} {Object.keys(savedTrial.answers ?? savedTrial.results ?? {}).length} {t('trial_from')} {savedTrial.quiz.length} {t('trial_questions')}
            </div>
          </div>
          <button onClick={resumeTrial} style={{
            background: 'linear-gradient(135deg,#7c6df8,#a78bfa)',
            border: 'none', borderRadius: 10, padding: '8px 16px',
            color: '#fff', fontSize: 13, fontWeight: 700,
            fontFamily: 'Tajawal,sans-serif', cursor: 'pointer',
          }}>{t('trial_resume')}</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", justifyContent: "center" }}>
        {TYPES.map(typ => (
          <button key={typ.id} onClick={() => { if (!typ.comingSoon) { setMode(typ.id); setTahsiliSubject(null); setQudratSub(null); } }} style={{
            padding: "9px 16px", borderRadius: 50, border: "2px solid",
            borderColor: typ.comingSoon ? "var(--border)" : mode === typ.id ? typ.color : "var(--border)",
            background: typ.comingSoon ? "transparent" : mode === typ.id ? typ.color + "22" : "transparent",
            color: typ.comingSoon ? "var(--text-muted)" : mode === typ.id ? typ.color : "var(--text-secondary)",
            cursor: typ.comingSoon ? "not-allowed" : "pointer",
            fontSize: 13, fontFamily: "Tajawal,sans-serif", fontWeight: 700,
            transition: "all .2s", position: "relative",
            opacity: typ.comingSoon ? 0.6 : 1,
          }}>
            {typ.icon} {typ.label}
            {typ.comingSoon && (
              <span style={{
                position: "absolute", top: -8, left: -4,
                background: "#ef4444", color: "#fff", fontSize: 9,
                padding: "1px 5px", borderRadius: 50, fontWeight: 900,
              }}>{t('trial_comingSoon')}</span>
            )}
          </button>
        ))}
      </div>

      {/* شريط أقسام قدرات */}
      {mode === "الكل" && (
        <div style={{
          background: 'rgba(124,109,248,0.07)',
          border: '1px solid rgba(124,109,248,0.25)',
          borderRadius: 14, padding: '12px 16px', marginBottom: 16,
          direction: 'rtl',
        }}>
          <div style={{ color: '#7c6df8', fontSize: 12, fontWeight: 800, marginBottom: 10 }}>
            {t('trial_chooseSection')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { id: null,    labelKey: 'trial_all',   icon: '📚' },
              { id: 'كمي',   labelKey: 'trial_kmi',   icon: '🔢' },
              { id: 'لفظي',  labelKey: 'trial_lafzi', icon: '📖' },
            ].map(sub => (
              <button key={String(sub.id)} onClick={() => setQudratSub(sub.id)} style={{
                padding: '6px 14px', borderRadius: 50, border: '1.5px solid',
                borderColor: qudratSub === sub.id ? '#7c6df8' : 'rgba(124,109,248,0.3)',
                background: qudratSub === sub.id ? '#7c6df822' : 'transparent',
                color: qudratSub === sub.id ? '#7c6df8' : 'var(--text-secondary)',
                fontSize: 12, fontFamily: 'Tajawal,sans-serif', fontWeight: 700, cursor: 'pointer',
              }}>
                {sub.icon} {t(sub.labelKey)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* شريط أقسام التحصيلي */}
      {mode === "تحصيلي" && (
        <div style={{
          background: 'rgba(16,185,129,0.07)',
          border: '1px solid rgba(16,185,129,0.25)',
          borderRadius: 14, padding: '12px 16px', marginBottom: 16,
          direction: 'rtl',
        }}>
          <div style={{ color: '#10b981', fontSize: 12, fontWeight: 800, marginBottom: 10 }}>
            {t('trial_chooseSubject')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* زر الكل */}
            <button
              onClick={() => setTahsiliSubject(null)}
              style={{
                background: !tahsiliSubject ? 'rgba(129,140,248,0.25)' : 'rgba(129,140,248,0.08)',
                border: `1px solid ${!tahsiliSubject ? '#7c6df8' : 'rgba(129,140,248,0.3)'}`,
                borderRadius: 20, padding: '5px 12px',
                color: '#7c6df8', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                transition: 'all .15s',
              }}>📚 {t('trial_all')}</button>

            {TAHSILI_SUBJECTS.map(s => {
              const active = tahsiliSubject === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setTahsiliSubject(active ? null : s.id)}
                  style={{
                    background: active ? `${s.color}30` : `${s.color}10`,
                    border: `1px solid ${active ? s.color : `${s.color}44`}`,
                    borderRadius: 20, padding: '5px 12px',
                    color: s.color, fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
                    transition: 'all .15s',
                    transform: active ? 'scale(1.05)' : 'scale(1)',
                  }}
                >{s.icon} {s.id}</button>
              );
            })}
          </div>
          {tahsiliSubject && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 8 }}>
              ✅ {t('trial_fetchingSubject').replace('{sub}', tahsiliSubject)}
            </div>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20 }}>
        {[["📝","20",t('trial_questions')],["⏱️",`40-70${t('trial_secUnit')}`,t('trial_perQ')],["💡",t('trial_explainLabel'),t('trial_perAnswer')]].map(([ic,v,l]) => (
          <div key={l} style={{
            background: "var(--bg-card)", borderRadius: 14, padding: 14,
            textAlign: "center", border: "1px solid #334155",
          }}>
            <div style={{ fontSize: 22 }}>{ic}</div>
            <div style={{ color: "#7c6df8", fontWeight: 900, fontSize: 17 }}>{v}</div>
            <div style={{ color: "var(--text-secondary)", fontSize: 11 }}>{l}</div>
          </div>
        ))}
      </div>


      <button onClick={startQuiz} style={{
        width: "100%", background: "linear-gradient(135deg,#7c6df8,#a78bfa)",
        color: "#fff", border: "none", borderRadius: 14, padding: 17, fontSize: 19,
        fontWeight: 900, cursor: "pointer", fontFamily: "Tajawal,sans-serif",
        boxShadow: "0 0 24px #7c6df844", marginBottom: 12,
      }}>{t('trial_start')}</button>

      {onSkip && (
        <button onClick={onSkip} style={{
          width: "100%", background: "none", border: "1px solid #334155",
          borderRadius: 12, padding: 12, color: "var(--text-secondary)", fontSize: 13,
          fontFamily: "Tajawal,sans-serif", cursor: "pointer",
          marginBottom: 10,
        }}>{t('trial_skip')}</button>
      )}

      {onLogin && (
        <button onClick={onLogin} style={{
          width: "100%", background: "none", border: "none",
          color: "#7c6df8", fontSize: 13, fontFamily: "Tajawal,sans-serif",
          cursor: "pointer", padding: "8px 0",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <span style={{
            background: "rgba(129,140,248,0.12)",
            border: "1px solid rgba(129,140,248,0.25)",
            borderRadius: 20, padding: "5px 14px",
            fontSize: 13, fontWeight: 700,
          }}>{t('trial_login')}</span>
        </button>
      )}
    </div>
      </div> {/* end maxW */}
      </div> {/* end centered */}
    </div>
  );

  // ── Loading ──
  if (screen === "loading") {
    return (
      <div style={{ height:'100dvh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        background:'var(--bg-page)', color:'var(--text)', fontFamily:'Tajawal,sans-serif', gap:16 }}>
        <div style={{ fontSize:40 }}>⏳</div>
        <div style={{ fontSize:18, fontWeight:700 }}>{t('trial_loading')}</div>
        <div style={{ color:'var(--text-secondary)', fontSize:14 }}>من بنك أسئلة أ.فيصل</div>
      </div>
    );
  }

  // ── Quiz ──
  if (screen === "quiz") {
    const q = quiz[cur];
    const clr = COLORS[q.type] || "#7c6df8";
    const isStep = false;
    return wrap(
      <div>
        {/* شبكة الأسئلة */}
        {showGrid && (
          <QuestionGrid
            quiz={quiz}
            answers={answers}
            cur={cur}
            onNavigate={navigateTo}
            onClose={() => setShowGrid(false)}
          />
        )}

        {/* زر الخروج */}
        <button onClick={() => setShowExitDialog(true)} style={{
          position: 'fixed', top: 14, right: 16, zIndex: 9999,
          background: '#ef4444', border: 'none', color: '#fff',
          fontSize: 15, fontWeight: 900, cursor: 'pointer',
          borderRadius: 10, padding: '8px 18px',
          fontFamily: 'Tajawal,sans-serif',
          boxShadow: '0 4px 16px rgba(239,68,68,0.6)',
        }}>{t('trial_exit')}</button>

        {/* نافذة تأكيد الخروج */}
        {showExitDialog && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            padding: '0 0 24px',
          }}>
            <div style={{
              background: 'var(--bg-page)',
              border: '1px solid #334155', borderRadius: 24, padding: '28px 24px 20px',
              width: '100%', maxWidth: 440,
              direction: 'rtl', fontFamily: 'Tajawal,sans-serif',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.5)',
            }}>
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: 'rgba(239,68,68,0.12)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 26,
                }}>⚠️</div>
              </div>
              <h3 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 900, textAlign: 'center', margin: '0 0 6px' }}>
                {t('trial_exitTitle')}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', margin: '0 0 22px', lineHeight: 1.6 }}>
                {t('trial_answered')} {answeredCount} {t('trial_from')} {quiz.length} {t('trial_questions')}
              </p>
              <button onClick={() => setShowExitDialog(false)} style={{
                width: '100%', padding: '14px',
                background: 'linear-gradient(135deg,#7c6df8,#a78bfa)',
                border: 'none', borderRadius: 13, color: '#fff',
                fontSize: 15, fontWeight: 900, fontFamily: 'inherit',
                cursor: 'pointer', marginBottom: 10,
                boxShadow: '0 4px 18px #7c6df844',
              }}>{t('trial_continueExam')}</button>
              <button onClick={() => { setShowExitDialog(false); setScreen('home'); }} style={{
                width: '100%', padding: '13px',
                background: 'var(--bg-card-alt)',
                border: '1px solid var(--border)',
                borderRadius: 13, color: 'var(--text-secondary)',
                fontSize: 15, fontWeight: 700, fontFamily: 'inherit',
                cursor: 'pointer', marginBottom: 8,
              }}>{t('trial_saveAndExit')}</button>
              <button onClick={() => { localStorage.removeItem(TRIAL_KEY); setShowExitDialog(false); setScreen('home'); }} style={{
                width: '100%', padding: '11px',
                background: 'none', border: 'none',
                color: '#ef4444', fontSize: 14, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer',
              }}>{t('trial_exitNoSave')}</button>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{
            background: clr + "22", color: clr, padding: "4px 12px", borderRadius: 50,
            fontSize: 13, fontWeight: 700, border: `1px solid ${clr}44`,
          }}>
            {ICONS[q.type]} {q.type}{q.subject ? ` • ${q.subject}` : ""}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <button onClick={() => setShowGrid(true)} style={{
                background: 'rgba(129,140,248,0.12)',
                border: '1px solid rgba(129,140,248,0.3)',
                borderRadius: 8, padding: '4px 10px', cursor: 'pointer',
                color: '#7c6df8', fontSize: 12, fontWeight: 700,
                fontFamily: 'Tajawal,sans-serif',
              }}>📋 {answeredCount}/{quiz.length}</button>
              <span style={{ color: 'var(--text-muted)', fontSize: 10, whiteSpace: 'nowrap' }}>{t('trial_navigate')}</span>
            </div>
            <span style={{ color: "var(--text-secondary)", fontSize: 14 }}>{cur + 1} / {quiz.length}</span>
          </div>
        </div>

        {/* شريط التقدم */}
        <div style={{ background: "var(--bg-card)", borderRadius: 50, height: 6, marginBottom: 16, overflow: "hidden" }}>
          <div style={{
            background: `linear-gradient(90deg,${clr},#a78bfa)`, height: "100%",
            width: `${(answeredCount / quiz.length) * 100}%`, transition: "width .4s",
          }} />
        </div>

        {/* المؤقت */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <div style={{
            width: 54, height: 54, borderRadius: "50%", border: `4px solid ${timerC}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: timerC, fontSize: 20, fontWeight: 900, background: timerC + "11",
            transition: "all .5s",
          }}>{showAns ? "✓" : time}</div>
        </div>

        {/* السؤال */}
        <div style={{
          background: "var(--bg-card)", borderRadius: 18, padding: "20px 22px",
          marginBottom: 14, border: "1px solid #334155", direction: isStep ? "ltr" : "rtl",
        }}>
          <p style={{
            color: "var(--text)", fontSize: isStep ? 17 : 19, fontWeight: 700, margin: 0,
            lineHeight: 1.8, whiteSpace: "pre-line",
            fontFamily: isStep ? "Georgia,serif" : "Tajawal,sans-serif",
          }}>{q.text}</p>
        </div>

        {/* الخيارات */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
          {q.options.map((opt, i) => {
            let bg = "var(--bg-card)", border = "var(--border)", color = "#cbd5e1", fw = 500;
            if (showAns) {
              if (i === q.answer) { bg = "#14532d"; border = "#22c55e"; color = "#86efac"; fw = 700; }
              else if (i === sel) { bg = "#450a0a"; border = "#ef4444"; color = "#fca5a5"; }
            } else if (sel === i) { bg = "#312e81"; border = clr; color = "#c7d2fe"; fw = 700; }
            return (
              <button key={i} onClick={() => !showAns && submit(i)} style={{
                background: bg, border: `2px solid ${border}`, borderRadius: 12,
                padding: "13px 16px", color, fontSize: isStep ? 15 : 16, fontWeight: fw,
                cursor: showAns ? "default" : "pointer",
                fontFamily: isStep ? "Georgia,serif" : "Tajawal,sans-serif",
                textAlign: isStep ? "left" : "right", direction: isStep ? "ltr" : "rtl",
                display: "flex", alignItems: "center", gap: 10, transition: "all .25s",
              }}>
                <span style={{
                  background: "var(--bg-input)", borderRadius: 7, padding: "2px 9px",
                  fontSize: 12, color: "var(--text-muted)", minWidth: 24, textAlign: "center",
                  fontFamily: "Tajawal,sans-serif",
                }}>
                  {isStep ? ["A","B","C","D"][i] : ["أ","ب","ج","د"][i]}
                </span>
                <span style={{ flex: 1 }}>{opt}</span>
                {showAns && i === q.answer && <span>✅</span>}
                {showAns && i === sel && i !== q.answer && <span>❌</span>}
              </button>
            );
          })}
        </div>

        {showAns && (
          <>
            {q.image_url && (
              <img src={q.image_url} alt="صورة السؤال"
                style={{ marginTop: 8, marginBottom: 12, width: '100%', borderRadius: 10,
                  border: '1px solid #334155', objectFit: 'contain',
                  maxHeight: 280, background: '#fff' }}
              />
            )}
            {q.explain && !q.explain.includes('المصدر') && !q.explain.includes('تسريبات') && (
            <div style={{
              background: "var(--bg-input)", border: "1px solid #334155", borderRadius: 12,
              padding: "10px 14px", marginBottom: 12, direction: isStep ? "ltr" : "rtl",
            }}>
              <p style={{
                color: "#fbbf24", fontSize: 13, margin: 0,
                fontFamily: isStep ? "Georgia,serif" : "Tajawal,sans-serif",
              }}>💡 {q.explain}</p>
            </div>
            )}
            {(hintLoading || hint) && (
              <div style={{
                background: 'rgba(139,92,246,0.08)',
                border: '1px solid rgba(139,92,246,0.3)',
                borderRadius: 12, padding: '10px 14px', marginBottom: 12,
                direction: 'rtl',
              }}>
                {hintLoading ? (
                  <p style={{ color: '#a78bfa', fontSize: 13, margin: 0, fontFamily: 'Tajawal,sans-serif' }}>
                    ✨ جاري توليد الشرح...
                  </p>
                ) : (
                  <p style={{ color: '#c4b5fd', fontSize: 13, margin: 0, fontFamily: 'Tajawal,sans-serif' }}>
                    ✨ {hint}
                  </p>
                )}
              </div>
            )}
            <TrialReportButton questionId={q.id} />

            {/* تقييم السؤال */}
            <QuestionRatingTrial qKey={q.id || `idx_${cur}`} rated={ratings[q.id || `idx_${cur}`]} onRate={(k, r) => handleRate(q.id, r)} />

            <button onClick={next} style={{
              width: "100%", background: `linear-gradient(135deg,${clr},#a78bfa)`,
              color: "#fff", border: "none", borderRadius: 12, padding: 14, fontSize: 16,
              fontWeight: 900, cursor: "pointer", fontFamily: "Tajawal,sans-serif",
            }}>
              {Object.keys(answersRef.current).length >= quiz.length ? t('trial_viewResult') : t('trial_next')}
            </button>
          </>
        )}
      </div>
    , 600);
  }

  // ── Feedback ──
  if (screen === "feedback") return wrap(
    <div style={{ textAlign: "center" }}>
      {feedbackSent ? (
        <div>
          <div style={{ fontSize: 72, marginBottom: 12 }}>🙏</div>
          <h2 style={{ color: "var(--text)", fontSize: 24, fontWeight: 900, margin: "0 0 8px" }}>{t('trial_feedbackThanks')}</h2>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, margin: "0 0 28px" }}>{t('trial_feedbackHelped')}</p>
          <button onClick={() => onDone && onDone({ score, total: quiz.length, pct })} style={{
            width: "100%", padding: 14,
            background: "linear-gradient(135deg,#7c3aed,#a78bfa)",
            border: "none", borderRadius: 13, color: "#fff",
            fontSize: 16, fontWeight: 900, cursor: "pointer", fontFamily: "Tajawal,sans-serif",
          }}>🔓 اشترك الآن</button>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 56, marginBottom: 10 }}>💬</div>
          <h2 style={{ color: "var(--text)", fontSize: 22, fontWeight: 900, margin: "0 0 4px" }}>{t('trial_feedbackTitle')}</h2>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, margin: "0 0 24px" }}>{t('trial_feedbackSubtitle')}</p>

          <div style={{ display: "flex", justifyContent: "center", gap: 10, marginBottom: 8 }}>
            {[1,2,3,4,5].map(n => (
              <button key={n} onClick={() => setStars(n)}
                onMouseEnter={() => setHoverStar(n)}
                onMouseLeave={() => setHoverStar(0)}
                style={{
                  background: "none", border: "none", cursor: "pointer", fontSize: 42, padding: 0,
                  filter: (hoverStar || stars) >= n ? "none" : "grayscale(1) opacity(0.3)",
                  transform: (hoverStar || stars) >= n ? "scale(1.15)" : "scale(1)",
                  transition: "all .15s",
                }}>⭐</button>
            ))}
          </div>

          {(hoverStar || stars) > 0 && (
            <p style={{
              color: ["","#ef4444","#f97316","#f59e0b","#22c55e","#7c6df8"][(hoverStar||stars)],
              fontWeight: 700, fontSize: 15, margin: "0 0 16px",
            }}>
              {["","ضعيف 😕","مقبول 😐","جيد 🙂","جيد جداً 😊","ممتاز 🔥"][(hoverStar||stars)]}
            </p>
          )}

          <input value={reviewName} onChange={e => setReviewName(e.target.value)}
            placeholder="اسمك (اختياري — يظهر مع رأيك)"
            style={{
              width: "100%", boxSizing: "border-box",
              background: "var(--bg-card)", border: "1px solid #334155",
              borderRadius: 12, padding: "12px 14px",
              color: "var(--text)", fontSize: 14, fontFamily: "Tajawal,sans-serif",
              outline: "none", direction: "rtl", marginBottom: 10,
            }}
          />

          {/* المرحلة الدراسية */}
          <select
            value={reviewStage}
            onChange={e => { setReviewStage(e.target.value); setReviewSpec(''); }}
            style={{
              width: '100%', boxSizing: 'border-box', marginBottom: 10,
              background: 'var(--bg-card)', border: '1px solid #334155',
              borderRadius: 12, padding: '12px 14px',
              color: reviewStage ? 'var(--text)' : 'var(--text-secondary)',
              fontSize: 14, fontFamily: 'Tajawal,sans-serif',
              outline: 'none', direction: 'rtl', cursor: 'pointer',
            }}
          >
            <option value="">المرحلة الدراسية (اختياري)</option>
            <option value="ثانوي علمي">ثانوي علمي</option>
            <option value="ثانوي أدبي">ثانوي أدبي</option>
            <option value="طالب جامعي">طالب جامعي</option>
            <option value="أخرى">أخرى</option>
          </select>

          {/* التخصص — يظهر فقط لثانوي علمي أو أدبي */}
          {SPECS_TQ[reviewStage] && (
            <select
              value={reviewSpec}
              onChange={e => setReviewSpec(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', marginBottom: 10,
                background: 'var(--bg-card)', border: '1px solid #7c6df8',
                borderRadius: 12, padding: '12px 14px',
                color: reviewSpec ? 'var(--text)' : 'var(--text-secondary)',
                fontSize: 14, fontFamily: 'Tajawal,sans-serif',
                outline: 'none', direction: 'rtl', cursor: 'pointer',
              }}
            >
              <option value="">التخصص</option>
              {SPECS_TQ[reviewStage].map(sp => (
                <option key={sp} value={sp}>{sp}</option>
              ))}
            </select>
          )}

          <textarea value={comment} onChange={e => setComment(e.target.value)}
            placeholder="أي ملاحظات تريد مشاركتها؟ (اختياري)"
            rows={3}
            style={{
              width: "100%", boxSizing: "border-box",
              background: "var(--bg-card)", border: "1px solid #334155",
              borderRadius: 12, padding: "12px 14px",
              color: "var(--text)", fontSize: 14, fontFamily: "Tajawal,sans-serif",
              resize: "none", outline: "none", direction: "rtl", marginBottom: 16,
            }}
          />

          <button
            onClick={async () => {
              if (stars === 0) return;
              const fullStage = reviewStage
                ? (reviewSpec ? `${reviewStage} - ${reviewSpec}` : reviewStage)
                : '';
              await saveReview({ displayName: reviewName.trim() || 'مستخدم', stars, comment, stage: fullStage });
              setFeedbackSent(true);
            }}
            disabled={stars === 0}
            style={{
              width: "100%", padding: 14,
              background: stars > 0 ? "linear-gradient(135deg,#7c6df8,#a78bfa)" : "var(--bg-card)",
              border: "none", borderRadius: 13,
              color: stars > 0 ? "#fff" : "var(--text-muted)",
              fontSize: 15, fontWeight: 900,
              cursor: stars > 0 ? "pointer" : "default",
              fontFamily: "Tajawal,sans-serif", marginBottom: 10, transition: "all .2s",
            }}
          >{stars === 0 ? t('trial_feedbackChoose') : t('trial_feedbackSend')}</button>

          <button onClick={() => onDone && onDone({ score, total: quiz.length, pct })} style={{
            width: "100%", background: "none", border: "none",
            color: "var(--text-muted)", fontSize: 13, cursor: "pointer",
            fontFamily: "Tajawal,sans-serif", padding: 8,
          }}>{t('trial_feedbackSkip')}</button>
        </div>
      )}
    </div>
  );

  // ── تحليل AI للاختبار التجريبي ──────────────────────────
  const handleAIAnalysis = async () => {
    if (aiLoading || aiAnalysis) return;
    setAiLoading(true);

    const wrongBySubject   = {};
    const correctBySubject = {};
    Object.entries(answersRef.current).forEach(([idx, ans]) => {
      const q   = quiz[parseInt(idx)];
      if (!q) return;
      const sub = q.subject || q.type || 'عام';
      if (ans.isCorrect) correctBySubject[sub] = (correctBySubject[sub] || 0) + 1;
      else               wrongBySubject[sub]   = (wrongBySubject[sub]   || 0) + 1;
    });

    try {
      const res  = await fetch('/api/analyze', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode:            mode === 'الكل' ? 'قدرات' : mode,
          totalQuestions:  quiz.length,
          correctCount:    score,
          score:           pct,
          wrongBySubject,
          correctBySubject,
          durationSecs:    0,
        }),
      });
      const data = await res.json();
      setAiAnalysis(data.analysis || 'تعذّر توليد التحليل.');
    } catch {
      setAiAnalysis('تعذّر الاتصال بالخادم.');
    } finally {
      setAiLoading(false);
    }
  };

  // ── Result ──
  const medal = pct >= 90 ? "🏆" : pct >= 70 ? "🥈" : pct >= 50 ? "💪" : "📚";
  const msg = pct >= 90 ? "ممتاز! أنت قادر فعلاً 🔥" : pct >= 70 ? "جيد جداً! واصل التدريب" : pct >= 50 ? "مقبول، تحتاج مزيداً من التمرين" : "راجع المادة وحاول مجدداً 💪";
  const msgC = pct >= 90 ? "#86efac" : pct >= 70 ? "#93c5fd" : pct >= 50 ? "#fbbf24" : "#fca5a5";

  return wrap(
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 64 }}>{medal}</div>
      <h2 style={{ color: "var(--text)", fontSize: 26, fontWeight: 900, margin: "8px 0" }}>{t('trial_result')}</h2>

      <div style={{
        background: "var(--bg-card)", borderRadius: 22, padding: 26,
        border: "1px solid #334155", marginBottom: 14,
      }}>
        <div style={{
          fontSize: 62, fontWeight: 900,
          background: "linear-gradient(90deg,#7c6df8,#a78bfa)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>{pct}%</div>
        <p style={{ color: "var(--text-secondary)", fontSize: 15 }}>{score} {t('trial_correctFrom')} {quiz.length}</p>
        <div style={{ background: "var(--bg-input)", borderRadius: 10, height: 10, overflow: "hidden", margin: "10px 0" }}>
          <div style={{
            background: pct >= 70 ? "#22c55e" : pct >= 50 ? "#f59e0b" : "#ef4444",
            height: "100%", width: `${pct}%`, borderRadius: 10, transition: "width 1s",
          }} />
        </div>
        <p style={{ color: msgC, fontWeight: 700, fontSize: 16, margin: 0 }}>{msg}</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 14 }}>
        {["كمي","لفظي","تحصيلي"].map(t => {
          const qs = quiz.map((q, i) => ({ ...q, ans: answersRef.current[i] })).filter(q => q.type === t);
          const c = qs.filter(q => q.ans?.isCorrect).length;
          if (qs.length === 0) return null;
          return (
            <div key={t} style={{
              background: "var(--bg-card)", borderRadius: 12, padding: 12,
              border: `1px solid ${COLORS[t]}33`,
            }}>
              <div style={{ fontSize: 18 }}>{ICONS[t]}</div>
              <div style={{ color: COLORS[t], fontWeight: 700, fontSize: 13 }}>
                {t}
              </div>
              <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>{c}/{qs.length}</div>
            </div>
          );
        })}
      </div>

      {/* ── تحليل AI ── */}
      <div style={{ marginBottom: 12, textAlign: 'right' }}>
        {!aiAnalysis && !aiLoading && (
          <button onClick={handleAIAnalysis} style={{
            width: '100%',
            background: 'linear-gradient(135deg,rgba(139,92,246,0.12),rgba(124,109,248,0.08))',
            border: '1.5px solid rgba(139,92,246,0.4)',
            borderRadius: 14, padding: '14px 18px',
            cursor: 'pointer', fontFamily: 'Tajawal,sans-serif',
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 0,
          }}>
            <span style={{ fontSize: 24 }}>🤖</span>
            <div style={{ textAlign: 'right', flex: 1 }}>
              <div style={{ color: '#a78bfa', fontWeight: 900, fontSize: 14 }}>{t('trial_aiAnalyze')}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>{t('trial_aiStrengths')}</div>
            </div>
            <span style={{ color: '#a78bfa' }}>✨</span>
          </button>
        )}

        {aiLoading && (
          <div style={{
            background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)',
            borderRadius: 14, padding: '16px', textAlign: 'center',
          }}>
            <div style={{ color: '#a78bfa', fontSize: 14, fontFamily: 'Tajawal,sans-serif' }}>
              {t('trial_aiAnalyzing')}
            </div>
          </div>
        )}

        {aiAnalysis && (
          <div style={{
            background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.3)',
            borderRadius: 14, padding: '16px 18px',
            direction: 'rtl', fontFamily: 'Tajawal,sans-serif', textAlign: 'right',
          }}>
            <div style={{ color: '#a78bfa', fontWeight: 900, fontSize: 13, marginBottom: 10 }}>
              {t('trial_aiTitle')}
            </div>
            {aiAnalysis.split('\n').filter(l => l.trim()).map((line, i) => {
              const isHeader = line.startsWith('💪') || line.startsWith('⚠️') || line.startsWith('📌');
              return (
                <p key={i} style={{
                  margin: isHeader ? '10px 0 3px' : '0 0 3px',
                  color: isHeader ? 'var(--text)' : 'var(--text-secondary)',
                  fontSize: isHeader ? 13 : 12,
                  fontWeight: isHeader ? 800 : 400,
                  lineHeight: 1.7,
                }}>{line}</p>
              );
            })}
          </div>
        )}
      </div>

      <button onClick={() => setScreen("feedback")} style={{
        width: "100%",
        background: "linear-gradient(135deg,#7c3aed,#a78bfa)", color: "#fff",
        border: "none", borderRadius: 12, padding: 14, fontSize: 16,
        fontWeight: 900, cursor: "pointer", fontFamily: "Tajawal,sans-serif",
        marginBottom: 10,
      }}>{t('trial_subscribe')}</button>

      {/* شارك نتيجتك */}
      <button onClick={() => {
        const filled = Math.round(pct / 20);
        const stars = '⭐'.repeat(filled) + '▫️'.repeat(5 - filled);
        const headline = pct >= 90 ? `🏆 ${pct}% — مستوى متقدم!` : pct >= 75 ? `🔥 ${pct}% — أداء قوي جداً` : pct >= 60 ? `💪 ${pct}% — فوق المتوسط` : pct >= 40 ? `📈 ${pct}% — في الطريق الصح` : `🎯 ${pct}% — البداية من هنا`;
        const challenge = pct >= 70 ? 'هل تقدر تتجاوزني؟ 👀' : 'أتحداك تجرّب! 💬';
        const text = `${headline}\n${stars}\n\n📋 ${score} من ${quiz.length} إجابة صحيحة\n📚 اختبار تجريبي — قدرات\n\n${challenge}\n👇 جرّب مجاناً\nqadder.sa`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
      }} style={{
        width: "100%",
        background: "linear-gradient(135deg,#25d366,#128c7e)",
        border: "none", borderRadius: 12, padding: "12px 14px",
        cursor: "pointer", fontFamily: "Tajawal,sans-serif",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        marginBottom: 10,
      }}>
        <span style={{ fontSize: 18 }}>📤</span>
        <span style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>{t('trial_shareWhatsapp')}</span>
      </button>

      {/* زر كتابة الرأي منفصل */}
      <button onClick={() => setScreen("feedback")} style={{
        width: "100%",
        background: "rgba(251,191,36,0.08)",
        border: "1px solid rgba(251,191,36,0.3)",
        borderRadius: 12, padding: "12px 14px",
        cursor: "pointer", fontFamily: "Tajawal,sans-serif",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        marginBottom: 10,
      }}>
        <span style={{ fontSize: 20 }}>⭐</span>
        <span style={{ color: "#fbbf24", fontSize: 14, fontWeight: 700 }}>{t('trial_writeReview')}</span>
      </button>

      <button onClick={() => { setScreen("home"); setQuiz([]); setAnswers({}); answersRef.current = {}; }} style={{
        width: "100%", background: "var(--bg-card)", color: "var(--text-secondary)",
        border: "1px solid #334155", borderRadius: 10, padding: "10px 26px",
        fontSize: 13, cursor: "pointer", fontFamily: "Tajawal,sans-serif",
      }}>{t('trial_newTest')}</button>
    </div>
  );
}
